import { useCallback, useContext, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import GeoContext from 'contexts/geoContext'
import CurrencyContext from 'contexts/currencyContext'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import FilterChip from 'LuxKit/Chips/FilterChip'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import useExperiencesSortOptions from 'components/Experiences/hooks/useExperiencesSortOptions'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { replaceExperienceQueryString } from 'lib/experiences/experienceUtils'
import { buildUrl } from 'lib/url/searchUrlUtils'
import TripDrawer, { type TripDrawerAnimation } from 'tripPlanner/components/GenericDrawer/TripDrawer'
import TripDrawerHeaderTitle from 'tripPlanner/components/GenericDrawer/TripDrawerHeaderTitle'
import ListingDrawerHeaderActions from '../ListingDrawerHeaderActions'
import AddToTripAction from 'tripPlanner/components/GenericDrawer/AddToTripAction'
import ExperienceDetailView from './ExperienceDetailView'
import ExperienceSearchResults from './ExperienceSearchResults'
import type { DrawerExperienceSearchTile } from './ExperienceDetailView'
import { mapExperienceDetailToDrawer } from './mapExperienceDetailToDrawer'
import { formatExperienceHeaderSubtitle } from './formatExperienceHeaderSubtitle'
import useExperienceDetail from '../../hooks/useExperienceDetail'
import useExperienceDetails from '../../hooks/useExperienceDetails'
import useOfferReviews from '../../hooks/useOfferReviews'
import useExperienceSearch from './useExperienceSearch'
import type { TripItineraryData, TripStop } from 'tripPlanner/api/types'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import { useExperiences } from 'tripPlanner/hooks/api/offer'
import uuidV4 from 'lib/string/uuidV4Utils'
import useTripPlannerOfferListTracking from 'tripPlanner/hooks/useTripPlannerOfferListTracking'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import { getSavedExperienceOfferIds } from 'tripPlanner/utils/chatItinerary'
import { resolveSearchDefaults } from 'tripPlanner/utils/resolveSearchDefaults'
import { buildExperienceRecentSearchUpsertBody } from 'tripPlanner/utils/tripChatRecentSearchPayload'
import usePersistTripChatRecentSearch from 'tripPlanner/hooks/usePersistTripChatRecentSearch'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { useTripPlannerEventBusOptional } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { buildExperienceSearchSubmittedActivityEntry } from './buildExperienceSearchSubmittedActivityEntry'
import { useOfferDrawerOpenedDispatch } from '../../hooks/useOfferDrawerOpenedDispatch'
import { buildLastExperienceSearchCommittedStateEntry } from './buildLastExperienceSearchCommittedStateEntry'
import { arrayToMap, partitionBy } from 'lib/array/arrayUtils'
import {
  shouldRenderTripDrawerTitleInContent,
  useRightPanelTripDrawerPresentation,
} from 'tripPlanner/components/GenericDrawer/TripDrawerPresentationContext'
import TripDrawerAssistantBridge, { TripDrawerAssistantFooter } from '../TripDrawerAssistantBridge'
import ListingDrawerSearchControls, {
  type DrawerSortValue,
  type ListingDrawerSearchSubmitParams,
} from '../ListingDrawerSearchControls'
import type { DrawerSortOption } from '../DrawerSortControl'
import useExperienceListingSearchState from './useExperienceListingSearchState'
import { compactLines } from '../../utils/drawerAskAIContext'
import { formatTripDrawerMapLabel } from '../formatTripDrawerMapLabel'
import { DrawerBodyContentWrapper } from '../drawerBodyLayout.styles'

const MAX_EXPERIENCE_SEARCH_RESULTS_IN_CONTEXT = 3

const ContentWrapper = DrawerBodyContentWrapper

const FullWidthLoadingBox = styled(LoadingBox)`
  width: 100%;
`

function formatExperienceSearchResult(
  experience: DrawerExperienceSearchTile,
  index: number,
  currencyCode: string,
): string {
  const price = experience.price !== undefined
    ? `, from ${currencyCode} ${experience.price}`
    : ''
  return `Result ${index + 1}: ${experience.name}${price}`
}

export interface ExperienceListingDrawerProps {
  isOpen: boolean
  onClose: () => void
  placeName: string
  /** Absent when the drawer is opened for a bare place (e.g. a breadcrumb). */
  placeCountry?: string
  /** LE place id for `placeName`, when the opener resolved one. */
  placeId?: string
  /** Seed the search from `placeName` rather than from the trip's recent
   *  searches. Every stop-less drawer shares the one `tripStopId: null` recent
   *  search, so without this the second place opened re-runs the first's
   *  search instead of its own. */
  seedSearchFromPlace?: boolean
  /** Back out of a drawer stacked on top of another one. The drawer's own
   *  list <-> detail back takes priority while it has somewhere to return to. */
  onBack?: () => void
  onAfterClose?: () => void
  animation?: TripDrawerAnimation
  stopCoordinates?: { lat: number; lng: number }
  onExploreMap?: () => void
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
  onShowExperienceOnMap?: (experience: {
    offerId: string
    name: string
    geoLocation: { lat: number; lng: number }
  }) => void
  contextStop?: TripStop
  initialSelectedOfferId?: string
}

function ExperienceListingDrawer({
  isOpen,
  onClose,
  placeName,
  placeCountry,
  placeId,
  seedSearchFromPlace,
  stopCoordinates,
  onBack,
  onAfterClose,
  animation,
  onExploreMap,
  itineraryData,
  onSendMessage,
  onShowExperienceOnMap,
  contextStop,
  initialSelectedOfferId,
}: ExperienceListingDrawerProps) {
  const intl = useIntl()
  const geo = useContext(GeoContext)
  const currency = useContext(CurrencyContext)
  const { chatId } = useChatStreamStatus()
  const persistRecentSearch = usePersistTripChatRecentSearch(chatId)
  const eventBus = useTripPlannerEventBusOptional()
  const drawerPresentation = useRightPanelTripDrawerPresentation()
  const isMobileScreen = useScreenSizeOnly('mobile')
  const { sortOptions: experienceSortOptions } = useExperiencesSortOptions()
  const [selectedOfferId, setSelectedOfferId] = useState<string | undefined>(undefined)
  // True only when the detail view was reached by selecting an experience from the
  // listing, so "back" has a listing to return to. Opened straight into detail
  // (initialSelectedOfferId, e.g. from the timeline) shows a close (X) instead.
  const [detailFromListing, setDetailFromListing] = useState(false)
  const [selectedExperienceName, setSelectedExperienceName] = useState<string | undefined>(
    undefined,
  )
  const [drawerSessionKey, setDrawerSessionKey] = useState(() => uuidV4())
  const handleOpenReset = useCallback(() => {
    setDrawerSessionKey(uuidV4())
    setSelectedOfferId(initialSelectedOfferId)
    setDetailFromListing(false)
    setSelectedExperienceName(undefined)
  }, [initialSelectedOfferId])
  const {
    browseAllResultsUrl,
    fallbackPlaceIds,
    handleDateChange,
    handleSearch,
    hasUnresolvedDestinationSearch,
    isLocationOrDateRefined,
    isRefinedSearch,
    resolvedDestination,
    resolvedExperienceSearchPlace,
    resolvedPlaceId,
    searchLocation,
    searchQuery,
    selectedDateRange,
    setSortValue,
    sortValue,
    tripDateRange,
  } = useExperienceListingSearchState({
    isOpen,
    placeName,
    placeCountry,
    placeId,
    seedSearchFromPlace,
    stopCoordinates,
    contextStop,
    itineraryData,
    regionCode: geo.currentRegionCode,
    onOpenReset: handleOpenReset,
  })

  const searchCalendarFocusDate = useMemo(() => {
    const resolved = resolveSearchDefaults({
      surface: 'experiences',
      stopId: contextStop?.id ?? null,
      recentSearches: itineraryData?.recentSearches,
      indicativeSearchMonth: itineraryData?.indicativeSearchMonth,
      selected: { dateRange: selectedDateRange, sort: sortValue },
      concrete: {
        checkIn: contextStop?.startDate ?? contextStop?.days.find(day => day.date)?.date,
        checkOut: contextStop?.endDate,
        locationQuery: [placeName, placeCountry].filter(Boolean).join(', '),
      },
    })

    return resolved.surface === 'experiences' ? resolved.focusMonth : new Date()
  }, [
    contextStop,
    itineraryData?.indicativeSearchMonth,
    itineraryData?.recentSearches,
    placeCountry,
    placeName,
    selectedDateRange,
    sortValue,
  ])

  const handleSearchSubmit = useCallback((params: ListingDrawerSearchSubmitParams) => {
    const submittedPlaceId = params.searchItem?.searchType === 'destination'
      ? params.searchItem.value
      : resolvedPlaceId

    const dateRange = {
      checkIn: params.hotelDateSelection.type === 'specific'
        ? params.hotelDateSelection.checkIn
        : undefined,
      checkOut: params.hotelDateSelection.type === 'specific'
        ? params.hotelDateSelection.checkOut
        : undefined,
    }

    persistRecentSearch(buildExperienceRecentSearchUpsertBody({
      tripStopId: contextStop?.id ?? null,
      searchQuery: params.searchValue,
      placeId: submittedPlaceId,
      dateRange,
      sort: params.sortValue,
    }))
    eventBus?.dispatch({
      type: 'trip_planner_surface_activity',
      entries: [buildExperienceSearchSubmittedActivityEntry(params.searchValue, dateRange, params.occupancies)],
    })
    // A place-opened drawer (a breadcrumb) has no stop, and there's no flat-key
    // fallback here the way flight search has one -- so it commits nothing.
    if (contextStop?.id !== undefined) {
      eventBus?.dispatch({
        type: 'trip_planner_committed_state',
        entries: [buildLastExperienceSearchCommittedStateEntry(contextStop.id, params.searchValue, dateRange, params.occupancies)],
      })
    }
  }, [contextStop?.id, eventBus, persistRecentSearch, resolvedPlaceId])

  const { experiences, searchContext, isLoading, isError, refetch } = useExperienceSearch({
    searchQuery,
    placeId: resolvedPlaceId,
    fallbackPlace: resolvedExperienceSearchPlace,
    fallbackPlaceIds,
    from: selectedDateRange.checkIn,
    to: selectedDateRange.checkOut,
    sortBy: sortValue,
    enabled: isOpen && !hasUnresolvedDestinationSearch,
  })
  const experienceOfferIds = useMemo(
    () => experiences.map((experience) => experience.offerId),
    [experiences],
  )
  const trackingOffers = useExperiences({
    currentCurrency: currency,
    experienceIds: isOpen ? experienceOfferIds : [],
    currentRegionCode: geo.currentRegionCode,
  })
  const trackingOfferById = useMemo(() => {
    return arrayToMap(trackingOffers.data, (offer) => offer.id)
  }, [trackingOffers.data])
  const getTrackingOffer = useCallback(
    (offerId: string) => {
      return trackingOfferById.get(offerId)
    },
    [trackingOfferById],
  )

  const {
    experienceDetail,
    isLoading: isDetailLoading,
    isError: isDetailError,
    refetch: refetchDetail,
  } = useExperienceDetail({
    offerId: selectedOfferId,
    currencyCode: currency,
    enabled: !!selectedOfferId,
  })

  const { reviews, isLoading: reviewsLoading } = useOfferReviews({
    offerId: selectedOfferId,
    region: geo.currentRegionCode,
    enabled: !!selectedOfferId && !!experienceDetail,
  })

  const detailExperience = useMemo(() => {
    if (!experienceDetail) return undefined
    return mapExperienceDetailToDrawer(experienceDetail)
  }, [experienceDetail])

  // See useOfferDrawerOpenedDispatch for the dedup rationale (also shared with HotelDetailView).
  useOfferDrawerOpenedDispatch(selectedOfferId, detailExperience?.name, 'experience')

  const defaultListHeaderTitle = intl.formatMessage(
    {
      id: 'V6Sb0Bka9c4FK9WOArYJ',
      defaultMessage: 'Experiences in {placeName}',
      description:
        'Heading for the experience listing drawer showing available experiences for a specific stop location.',
    },
    { placeName },
  )
  const refinedListHeaderTitle = intl.formatMessage({
    id: 'T8oIelE1tY_weHdZkoSC',
    defaultMessage: 'Search experiences',
    description:
      'Generic title for experience listing drawer after users refine the drawer search.',
  })
  const listHeaderTitle = isRefinedSearch ?
    refinedListHeaderTitle :
    defaultListHeaderTitle
  const isDetailView = !!selectedOfferId
  const shouldRenderTitleInContent =
    shouldRenderTripDrawerTitleInContent(drawerPresentation)
  const showDetailTitleInContent =
    isDetailView && shouldRenderTitleInContent
  const showListTitleInContent =
    !isDetailView && shouldRenderTitleInContent

  const drawerHeader = useMemo(() => {
    if (showDetailTitleInContent || showListTitleInContent) return undefined

    const title = selectedOfferId
      ? detailExperience?.name ?? selectedExperienceName
      : listHeaderTitle
    if (!title) return undefined

    const subtitle =
      selectedOfferId && detailExperience
        ? formatExperienceHeaderSubtitle(detailExperience)
        : undefined

    return <TripDrawerHeaderTitle title={title} subtitle={subtitle} />
  }, [
    detailExperience,
    listHeaderTitle,
    selectedExperienceName,
    selectedOfferId,
    showDetailTitleInContent,
    showListTitleInContent,
  ])

  const handleSelectExperience = useCallback(
    (experience: DrawerExperienceSearchTile) => {
      setSelectedOfferId(experience.offerId)
      setDetailFromListing(true)
      setSelectedExperienceName(experience.name)
    },
    [],
  )

  const handleBack = useCallback(() => {
    setSelectedOfferId(undefined)
    setDetailFromListing(false)
    setSelectedExperienceName(undefined)
  }, [])

  const handleClose = useCallback(() => {
    setSelectedOfferId(undefined)
    setDetailFromListing(false)
    setSelectedExperienceName(undefined)
    onClose()
  }, [onClose])

  const handleAfterClose = useCallback(() => {
    setSelectedOfferId(undefined)
    setDetailFromListing(false)
    setSelectedExperienceName(undefined)
  }, [])

  // The drawer's own reset first, then the owner's -- a stacked drawer's owner
  // pops it off the stack here, which unmounts everything below.
  const handleDrawerAfterClose = useCallback(() => {
    handleAfterClose()
    onAfterClose?.()
  }, [handleAfterClose, onAfterClose])

  const handleRetryDetail = useCallback(() => {
    refetchDetail()
  }, [refetchDetail])

  const handleRetrySearch = useCallback(() => {
    refetch()
  }, [refetch])

  const canGoBack = isDetailView && detailFromListing
  const savedExperienceStop = isLocationOrDateRefined ? undefined : contextStop
  const savedExperienceOfferIds = useMemo(() => {
    return getSavedExperienceOfferIds(savedExperienceStop)
  }, [savedExperienceStop])
  const hasSavedExperiences = savedExperienceOfferIds.size > 0

  // Split the live results into saved vs explore. A saved experience that is still
  // in the results keeps its rich search tile.
  const [savedSearchExperiences, exploreExperiences] = useMemo(() => {
    if (!hasSavedExperiences) return [[], experiences]
    return partitionBy(experiences, (experience) =>
      savedExperienceOfferIds.has(experience.offerId),
    )
  }, [experiences, hasSavedExperiences, savedExperienceOfferIds])

  const sortedExploreExperiences = useMemo(() => {
    if (sortValue !== 'bestValue') {
      return exploreExperiences
    }

    return [...exploreExperiences].sort(
      (left, right) => (right.discount ?? 0) - (left.discount ?? 0),
    )
  }, [exploreExperiences, sortValue])

  // Saved experiences are counted from itinerary state, so any saved offer that the
  // live search omits must be backfilled from its offer detail — otherwise the stop
  // tile shows "{n} saved" while the drawer's Saved section silently drops rows.
  const missingSavedOfferIds = useMemo(() => {
    const presentOfferIds = new Set(
      savedSearchExperiences.map((experience) => experience.offerId),
    )
    return Array.from(savedExperienceOfferIds).filter(
      (offerId) => !presentOfferIds.has(offerId),
    )
  }, [savedExperienceOfferIds, savedSearchExperiences])
  const { experienceDetails: missingSavedDetails } = useExperienceDetails({
    offerIds: missingSavedOfferIds,
    currencyCode: currency,
    enabled: isOpen,
  })
  const savedExperiences = useMemo(() => {
    return [
      ...savedSearchExperiences,
      ...missingSavedDetails.map(mapExperienceDetailToDrawer),
    ]
  }, [savedSearchExperiences, missingSavedDetails])
  const hasExperiences = experiences.length > 0 || savedExperiences.length > 0
  const savedHeading = intl.formatMessage({
    id: 'Kqe4G7DQZXoc84Lv-JBS',
    defaultMessage: 'Saved',
    description:
      'Section heading for saved experiences in the trip planner experience drawer',
  })
  const exploreHeading = intl.formatMessage({
    id: 'qiheP9joj-3erCzoLW6p',
    defaultMessage: 'Explore more',
    description:
      'Section heading above experience search results in the trip planner experience drawer',
  })
  const searchedPlaceName = searchContext?.searchedPlace?.name
  const searchedPlaceId = searchContext?.searchedPlace?.placeId
  const requestedPlaceName = searchContext?.requestedPlace?.name ?? placeName
  const finalBrowseAllResultsUrl = useMemo(() => {
    if (!searchContext?.isFallbackPlace || !searchedPlaceId || !searchedPlaceName) {
      return browseAllResultsUrl
    }

    return buildUrl(
      `/search/experiences?${replaceExperienceQueryString({
        destinationId: searchedPlaceId,
        destinationName: searchedPlaceName,
        from: selectedDateRange.checkIn,
        to: selectedDateRange.checkOut,
        sortBy: sortValue === 'type' ? undefined : sortValue,
      })}`,
      geo.currentRegionCode,
    )
  }, [
    browseAllResultsUrl,
    geo.currentRegionCode,
    searchContext?.isFallbackPlace,
    searchedPlaceId,
    searchedPlaceName,
    selectedDateRange.checkIn,
    selectedDateRange.checkOut,
    sortValue,
  ])
  const fallbackNotice = useMemo(() => {
    if (!searchContext?.isFallbackPlace || !searchedPlaceName) {
      return undefined
    }

    return intl.formatMessage(
      {
        id: '_q90rmryCo2PaJxR-5fc',
        defaultMessage:
          'No experiences available in {requestedPlaceName}. Showing experiences in {searchedPlaceName}.',
        description:
          'Notice shown when the trip planner experience drawer shows broader destination results after the selected stop has no experience results.',
      },
      {
        requestedPlaceName,
        searchedPlaceName,
      },
    )
  }, [
    intl,
    requestedPlaceName,
    searchContext?.isFallbackPlace,
    searchedPlaceName,
  ])

  const sortOptions = useMemo(() => {
    return Object.entries(experienceSortOptions).map(([value, option]) => ({
      value: value as DrawerSortValue,
      label: option.label,
    })) satisfies Array<DrawerSortOption<DrawerSortValue>>
  }, [experienceSortOptions])

  const handleShowMapFromHeader = useCallback(() => {
    if (detailExperience?.geoLocation) {
      onShowExperienceOnMap?.({
        offerId: detailExperience.offerId,
        name: detailExperience.name,
        geoLocation: detailExperience.geoLocation,
      })
    }
  }, [detailExperience, onShowExperienceOnMap])

  const listHeaderActions = isDetailView ? undefined : (
    <ListingDrawerHeaderActions
      showBrowseAllAction={hasExperiences && !isError && !hasUnresolvedDestinationSearch}
      browseAllResultsUrl={finalBrowseAllResultsUrl}
      onExploreMap={onExploreMap}
      isMobileScreen={isMobileScreen}
      vertical="experience"
    />
  )

  const headerActions = useMemo(() => {
    if (!isDetailView || !detailExperience) return undefined

    const showMapLabel = formatTripDrawerMapLabel(intl, drawerPresentation)

    return (
      <Group direction="horizontal" gap={8} verticalAlign="center">
        {detailExperience.geoLocation && (
          <FilterChip
            size="small"
            startIcon={<LineMapIcon />}
            onClick={handleShowMapFromHeader}
          >
            {showMapLabel}
          </FilterChip>
        )}
        {itineraryData && onSendMessage && (
          <AddToTripAction
            itineraryData={itineraryData}
            onSendMessage={onSendMessage}
            placeName={detailExperience.name}
            offerId={detailExperience.offerId}
            offerType="experience"
            coordinates={detailExperience.geoLocation}
            variant="outline"
          />
        )}
      </Group>
    )
  }, [
    detailExperience,
    drawerPresentation,
    handleShowMapFromHeader,
    intl,
    isDetailView,
    itineraryData,
    onSendMessage,
  ])

  const tracking = useTripPlannerOfferListTracking(
    'experienceSearchDrawer',
    [
      drawerSessionKey,
      searchQuery,
      resolvedPlaceId,
      selectedDateRange.checkIn,
      selectedDateRange.checkOut,
      sortValue,
    ].filter(Boolean).join(':'),
  )

  const shouldShowDetailLoadingTitle =
    showDetailTitleInContent && !!selectedExperienceName
  const assistantExperienceSearchDetails = !isDetailView
    ? compactLines([
        'Drawer: Experience search',
        `Status: ${isLoading ? 'searching' : isError ? 'error' : hasUnresolvedDestinationSearch ? 'unresolved destination' : hasExperiences ? 'searched' : 'no results'}`,
        `Destination: ${searchQuery}`,
        selectedDateRange.checkIn && `From: ${selectedDateRange.checkIn}`,
        selectedDateRange.checkOut && `To: ${selectedDateRange.checkOut}`,
        `Sort: ${sortValue}`,
        fallbackNotice && `Search note: ${fallbackNotice}`,
        `Saved results: ${savedExperiences.length}`,
        `Explore results visible: ${sortedExploreExperiences.length}`,
        sortedExploreExperiences.length > 0 && 'Top visible results:',
        ...sortedExploreExperiences
          .slice(0, MAX_EXPERIENCE_SEARCH_RESULTS_IN_CONTEXT)
          .map((experience, index) => formatExperienceSearchResult(experience, index, currency)),
      ])
    : undefined
  const assistantContextLabel = isDetailView ? detailExperience?.name : listHeaderTitle
  const assistantContextDetails = isDetailView ? undefined : assistantExperienceSearchDetails

  return (
    <TripDrawer
      isOpen={isOpen}
      onClose={handleClose}
      onAfterClose={handleDrawerAfterClose}
      onBack={canGoBack ? handleBack : onBack}
      animation={animation}
      direction="right-to-left"
      presentation={drawerPresentation}
      header={drawerHeader}
      footer={
        <TripDrawerAssistantFooter
          presentation={drawerPresentation}
          contextLabel={isDetailView ? detailExperience?.name : undefined}
          isOpen={isOpen}
        />
      }
      headerActions={isDetailView ? headerActions : listHeaderActions}
    >
      <TripDrawerAssistantBridge
        presentation={drawerPresentation}
        isOpen={isOpen && !!assistantContextLabel && !!assistantContextDetails}
        contextLabel={assistantContextLabel}
        contextDetails={assistantContextDetails}
      />
      {isDetailView && (
        <>
          {isDetailLoading && !detailExperience && (
            <ContentWrapper>
              <Group direction="vertical" gap={16}>
                <FullWidthLoadingBox height={200} borderRadius="M" />
                {shouldShowDetailLoadingTitle && (
                  <Heading variant="heading3">{selectedExperienceName}</Heading>
                )}
                {!shouldShowDetailLoadingTitle && (
                  <LoadingBox height={28} width={240} borderRadius="S" />
                )}
                <LoadingBox height={20} width={160} borderRadius="S" />
              </Group>
            </ContentWrapper>
          )}
          {isDetailError && !isDetailLoading && !detailExperience && (
            <ContentWrapper>
              <Group
                direction="vertical"
                gap={8}
                horizontalAlign="center"
                verticalAlign="center"
              >
                {showDetailTitleInContent && selectedExperienceName && (
                  <Heading variant="heading3">{selectedExperienceName}</Heading>
                )}
                <BodyText variant="medium" colour="critical">
                  {intl.formatMessage({
                    id: 'clAQJsTYp1RaYNVY1-Rz',
                    defaultMessage: 'Failed to load experience details.',
                    description:
                      'Error message shown when experience offer details fail to load in the drawer',
                  })}
                </BodyText>
                <TextButton
                  kind="secondary"
                  size="small"
                  onClick={handleRetryDetail}
                >
                  {intl.formatMessage({
                    id: 'q2nIV5-Sco41UZtxNQNg',
                    defaultMessage: 'Try again',
                    description:
                      'Button label to retry loading hotel offer details after an error',
                  })}
                </TextButton>
              </Group>
            </ContentWrapper>
          )}
          {detailExperience && (
            <ExperienceDetailView
              key={detailExperience.offerId}
              experience={detailExperience}
              onEnlargeMap={
                detailExperience.geoLocation && onShowExperienceOnMap
                  ? handleShowMapFromHeader
                  : undefined
              }
              standaloneOffer={trackingOfferById.get(detailExperience.offerId)}
              reviews={reviews}
              reviewsLoading={reviewsLoading}
              itineraryData={itineraryData}
              onSendMessage={onSendMessage}
              scrollToPricing={false}
              showTitleInContent={showDetailTitleInContent}
              showLocationInContent={showDetailTitleInContent}
            />
          )}
        </>
      )}

      {!isDetailView && (
        <ContentWrapper>
          <Group direction="vertical" gap={24}>
            {showListTitleInContent && (
              <Group direction="vertical" gap={4}>
                <Heading variant="heading3">{listHeaderTitle}</Heading>
                <BodyText variant="large">{searchLocation}</BodyText>
              </Group>
            )}

            <ListingDrawerSearchControls
              searchLabel={intl.formatMessage({
                id: 'to9wSYVjr1w5GayYemuZ',
                defaultMessage: 'Destination',
                description:
                  'Destination search input label in the trip planner experience drawer',
              })}
              searchValue={searchLocation}
              searchItem={resolvedDestination}
              searchVertical="experience"
              searchPlaceholder={intl.formatMessage({
                id: 'NCxoqvZemIIh28fGg6Ao',
                defaultMessage: 'Search destinations',
                description:
                  'Placeholder for destination search in the trip planner experience drawer',
              })}
              onSearch={handleSearch}
              onSearchSubmit={handleSearchSubmit}
              checkIn={selectedDateRange.checkIn}
              checkOut={selectedDateRange.checkOut}
              onDateChange={handleDateChange}
              sortValue={sortValue}
              sortOptions={sortOptions}
              onSortChange={setSortValue}
              showRefineControls={!hasUnresolvedDestinationSearch && (hasExperiences || isRefinedSearch)}
              whenLabel={intl.formatMessage({
                id: 'bR5cnLyI_fV2S_o1ZOev',
                defaultMessage: 'When',
                description:
                  'When date field label in the trip planner experience drawer',
              })}
              experienceTripDateRange={tripDateRange}
              searchCalendarFocusDate={searchCalendarFocusDate}
            />

            {fallbackNotice && (
              <BodyText variant="medium" colour="neutral-two">
                {fallbackNotice}
              </BodyText>
            )}

            {hasUnresolvedDestinationSearch && (
              <Group
                direction="vertical"
                gap={8}
                horizontalAlign="center"
                verticalAlign="center"
              >
                <BodyText variant="medium" weight="bold">
                  {intl.formatMessage({
                    id: 'G8TVNasI5fuKPlItCqx4',
                    defaultMessage: 'Choose a destination to search experiences.',
                    description:
                      'Message shown when typed experience drawer search text cannot be resolved to a destination.',
                  })}
                </BodyText>
                <BodyText variant="medium" colour="neutral-two" align="center">
                  {intl.formatMessage({
                    id: 'J8rN8TL0nN5dhwMQvE3o',
                    defaultMessage: 'Select a destination from the suggestions, or browse all available experiences.',
                    description:
                      'Supporting text shown when typed experience drawer search text cannot be resolved to a destination.',
                  })}
                </BodyText>
                <TextButton kind="secondary" size="small" href={finalBrowseAllResultsUrl}>
                  {intl.formatMessage({
                    id: 'FSj1C-BYIDWjyQkCoDuU',
                    defaultMessage: 'Browse experiences',
                    description: 'Button label linking to experience search when the drawer destination search cannot resolve typed text.',
                  })}
                </TextButton>
              </Group>
            )}

            {isError && !isLoading && !hasUnresolvedDestinationSearch && (
              <Group
                direction="vertical"
                gap={8}
                horizontalAlign="center"
                verticalAlign="center"
              >
                <BodyText variant="medium" weight="bold">
                  {intl.formatMessage({
                    id: '_prz987A-9P1umJt9l9D',
                    defaultMessage: 'Failed to load experiences.',
                    description:
                      'Error message shown when experience search results fail to load in the drawer.',
                  })}
                </BodyText>
                <TextButton
                  kind="secondary"
                  size="small"
                  onClick={handleRetrySearch}
                >
                  {intl.formatMessage({
                    id: 'MUzm0X4vh_i2I0FMkGBi',
                    defaultMessage: 'Try again',
                    description:
                      'Button label to retry loading experience search results in the drawer.',
                  })}
                </TextButton>
              </Group>
            )}

            {!isError && !hasUnresolvedDestinationSearch && (
              <OfferListEventsProvider tracking={tracking}>
                {isLoading && (
                  <ExperienceSearchResults
                    experiences={sortedExploreExperiences}
                    isLoading
                    onSelectExperience={handleSelectExperience}
                    placeName={placeName}
                    browseAllResultsUrl={finalBrowseAllResultsUrl}
                    itineraryData={itineraryData}
                    onSendMessage={onSendMessage}
                    getTrackingOffer={getTrackingOffer}
                  />
                )}
                {!isLoading && !hasExperiences && (
                  <ExperienceSearchResults
                    experiences={sortedExploreExperiences}
                    isLoading={false}
                    onSelectExperience={handleSelectExperience}
                    placeName={placeName}
                    browseAllResultsUrl={finalBrowseAllResultsUrl}
                    itineraryData={itineraryData}
                    onSendMessage={onSendMessage}
                    getTrackingOffer={getTrackingOffer}
                  />
                )}
                {!isLoading && hasExperiences && (
                  <Group direction="vertical" gap={16}>
                    {hasSavedExperiences && savedExperiences.length > 0 && (
                      <Group direction="vertical" gap={12}>
                        <Heading variant="heading4">{savedHeading}</Heading>
                        <ExperienceSearchResults
                          experiences={savedExperiences}
                          isLoading={false}
                          onSelectExperience={handleSelectExperience}
                          placeName={placeName}
                          browseAllResultsUrl={finalBrowseAllResultsUrl}
                          itineraryData={itineraryData}
                          onSendMessage={onSendMessage}
                          getTrackingOffer={getTrackingOffer}
                          positionOffset={0}
                        />
                      </Group>
                    )}
                    {sortedExploreExperiences.length > 0 && (
                      <Group direction="vertical" gap={12}>
                        {hasSavedExperiences && (
                          <Heading variant="heading4">
                            {exploreHeading}
                          </Heading>
                        )}
                        <ExperienceSearchResults
                          experiences={sortedExploreExperiences}
                          isLoading={false}
                          onSelectExperience={handleSelectExperience}
                          placeName={placeName}
                          browseAllResultsUrl={finalBrowseAllResultsUrl}
                          itineraryData={itineraryData}
                          onSendMessage={onSendMessage}
                          getTrackingOffer={getTrackingOffer}
                          positionOffset={savedExperiences.length}
                        />
                      </Group>
                    )}
                  </Group>
                )}
              </OfferListEventsProvider>
            )}
          </Group>
        </ContentWrapper>
      )}
    </TripDrawer>
  )
}

export default ExperienceListingDrawer
